import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import { useMessages } from '../../../contexts/MessagesContext';
import LoginGate from '../../common/LoginGate/LoginGate';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import Icon from '../../common/Icon/Icon';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import './Messages.css';

/**
 * MESSAGES - chat between traders.
 *
 * Left: your chats (Inbox / Requests / Archived, searchable by name).
 * Right: the open chat. On phones one pane shows at a time.
 *
 * URL: ?c=<conversation id> opens a chat; ?to=<user id> opens (or
 * starts) the chat with that trader; ?box=requests|archived.
 *
 * Polls while visible: the open chat every 4s, the list every 10s.
 * Rules (requests, verification, caps, blocks) live on the server;
 * this page shows whatever it says and why.
 */

const REPORT_REASONS = [
    ['scam', 'Scam or fraud'],
    ['harassment', 'Harassment'],
    ['spam', 'Spam'],
    ['no-show', "Didn't ship / no-show"],
    ['other', 'Something else'],
];
const MAX_LEN = 2000;

const api = async (method, url, body) => {
    const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
    });
    let data = {};
    try { data = await res.json(); } catch { }
    if (!res.ok) throw new Error(data.error || 'Something went wrong.');
    return data;
};

const dayLabel = (d) => {
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
};
const timeLabel = (d) => new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const listTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toDateString() === new Date().toDateString()
        ? timeLabel(d)
        : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function Avatar({ user, size = 36 }) {
    return (
        <AvatarFrame user={user}>
            <img
                className="msg-avatar"
                style={{ width: size, height: size }}
                src={user ? getProfilePhotoSrc(user) : PLACEHOLDER_DATA_URI}
                alt=""
                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
            />
        </AvatarFrame>
    );
}

function Messages() {
    const { user } = useUser();
    const navigate = useNavigate();
    const { requests: requestCount, refresh: refreshBadge } = useMessages();
    const [params, setParams] = useSearchParams();
    const convId = Number(params.get('c')) || null;
    const toUserId = Number(params.get('to')) || null;
    const box = ['requests', 'archived'].includes(params.get('box')) ? params.get('box') : 'inbox';

    // ---------- the list ----------
    const [list, setList] = useState(null);
    const [q, setQ] = useState('');
    const loadList = useCallback(() => {
        if (!user) return;
        api('GET', `/api/messages/conversations?box=${box}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`)
            .then(d => setList(d.conversations))
            .catch(() => setList(prev => prev || []));
    }, [user, box, q]);
    useEffect(() => {
        const t = setTimeout(loadList, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [loadList, q]);
    useEffect(() => {
        const id = setInterval(() => { if (!document.hidden) loadList(); }, 10000);
        return () => clearInterval(id);
    }, [loadList]);

    // ---------- ?to=<user>: open their chat, or get ready to start one ----------
    const [starter, setStarter] = useState(null); // {other, canStart, reason, asRequest}
    useEffect(() => {
        if (!user || !toUserId) { setStarter(null); return; }
        api('GET', `/api/messages/with/${toUserId}`)
            .then(d => {
                if (d.conversationId) {
                    setParams({ c: d.conversationId }, { replace: true });
                } else {
                    setStarter(d);
                }
            })
            .catch(e => setStarter({ other: null, canStart: false, reason: e.message }));
    }, [user, toUserId, setParams]);

    // ---------- the open chat ----------
    const [thread, setThread] = useState(null);
    const [older, setOlder] = useState([]);   // pages loaded by scrolling up
    const [hasMoreOlder, setHasMoreOlder] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);
    const stickToBottom = useRef(true);
    const lastReadSent = useRef(0);

    const loadThread = useCallback(async () => {
        if (!convId) return;
        try {
            const d = await api('GET', `/api/messages/conversations/${convId}`);
            setThread(d);
            setError(null);
            // Once a request is accepted (you replied or tapped Accept) the
            // chat lives in your inbox - follow it there
            if (box === 'requests' && d.accepted) setParams({ c: convId }, { replace: true });
            setHasMoreOlder(prev => (older.length ? prev : d.hasMore));
            // Read everything on screen (only while you're at the bottom)
            const lastId = d.messages.length ? d.messages[d.messages.length - 1].id : 0;
            if (stickToBottom.current && lastId > lastReadSent.current) {
                lastReadSent.current = lastId;
                api('POST', `/api/messages/conversations/${convId}/read`, { lastId })
                    .then(() => { refreshBadge(); loadList(); })
                    .catch(() => { });
            }
        } catch (e) {
            setError(e.message);
        }
    }, [convId, older.length, refreshBadge, loadList, box, setParams]);

    useEffect(() => {
        setThread(null);
        setOlder([]);
        setHasMoreOlder(false);
        setError(null);
        stickToBottom.current = true;
        lastReadSent.current = 0;
        if (convId) loadThread();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convId]);

    useEffect(() => {
        if (!convId) return undefined;
        const id = setInterval(() => { if (!document.hidden) loadThread(); }, 4000);
        return () => clearInterval(id);
    }, [convId, loadThread]);

    // All messages on screen: older pages + the latest page, by id
    const messages = (() => {
        const byId = new Map();
        [...older, ...(thread?.messages || [])].forEach(m => byId.set(m.id, m));
        return [...byId.values()].sort((a, b) => a.id - b.id);
    })();

    // Keep the view pinned to the newest message unless you scrolled up
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
    }, [messages.length, thread?.typing, convId]);

    const onScroll = async () => {
        const el = scrollRef.current;
        if (!el) return;
        stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        // Near the top: load the previous page, keeping your place
        if (el.scrollTop < 80 && hasMoreOlder && !loadingOlder && messages.length) {
            setLoadingOlder(true);
            const prevHeight = el.scrollHeight;
            try {
                const d = await api('GET', `/api/messages/conversations/${convId}?before=${messages[0].id}`);
                setOlder(o => [...d.messages, ...o]);
                setHasMoreOlder(d.hasMore);
                requestAnimationFrame(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevHeight;
                });
            } catch { }
            setLoadingOlder(false);
        }
    };

    // ---------- composer ----------
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const lastTypingPing = useRef(0);
    useEffect(() => { setDraft(''); setSendError(null); }, [convId, toUserId]);

    const onDraft = (v) => {
        setDraft(v.slice(0, MAX_LEN));
        if (convId && Date.now() - lastTypingPing.current > 3000) {
            lastTypingPing.current = Date.now();
            api('POST', `/api/messages/conversations/${convId}/typing`).catch(() => { });
        }
    };

    const send = async () => {
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        setSendError(null);
        try {
            if (convId) {
                await api('POST', `/api/messages/conversations/${convId}/messages`, { body });
                stickToBottom.current = true;
                await loadThread();
            } else if (toUserId) {
                const d = await api('POST', `/api/messages/with/${toUserId}`, { body });
                setParams({ c: d.conversationId });
            }
            setDraft('');
            loadList();
        } catch (e) {
            setSendError(e.message);
        } finally {
            setSending(false);
        }
    };

    // ---------- chat actions (menu) ----------
    const [menuOpen, setMenuOpen] = useState(false);
    const [dialog, setDialog] = useState(null);
    const [notice, setNotice] = useState(null);
    const [reportReason, setReportReason] = useState('spam');
    const [reportDetail, setReportDetail] = useState('');
    const other = thread?.other || starter?.other || null;

    useEffect(() => {
        if (!menuOpen) return undefined;
        const close = () => setMenuOpen(false);
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, [menuOpen]);

    const act = async (fn, after) => {
        try {
            await fn();
            if (after) after();
            loadThread();
            loadList();
            refreshBadge();
        } catch (e) {
            setNotice({ title: 'That didn’t work', message: e.message });
        }
    };

    const block = () => setDialog({
        title: `Block ${other.username}?`,
        message: 'They can’t message you or send trade offers, and you won’t see each other in trader lists. Offers still waiting between you are withdrawn; trades already shipping carry on. You can unblock any time.',
        confirmLabel: 'Block',
        danger: true,
        action: () => act(() => api('POST', `/api/blocks/${other.id}`)),
    });
    const unblock = () => act(() => api('DELETE', `/api/blocks/${other.id}`));
    const deleteChat = () => setDialog({
        title: 'Delete this chat?',
        message: `It disappears from your chats. ${other?.username || 'They'} keep${other ? 's' : ''} their copy. If they message again, it starts fresh.`,
        confirmLabel: 'Delete chat',
        danger: true,
        action: () => act(() => api('DELETE', `/api/messages/conversations/${convId}`),
            () => setParams(box !== 'inbox' ? { box } : {})),
    });
    const decline = () => setDialog({
        title: 'Decline this message request?',
        message: `It leaves your requests and ${other?.username || 'they'} can't send you more. If you ever message them yourself, the chat opens again.`,
        confirmLabel: 'Decline',
        danger: true,
        action: () => act(() => api('POST', `/api/messages/conversations/${convId}/decline`),
            () => setParams(box !== 'inbox' ? { box } : {})),
    });
    const report = () => {
        setReportReason('spam');
        setReportDetail('');
        setDialog({
            kind: 'report',
            title: `Report ${other.username}`,
            confirmLabel: 'File report',
            danger: true,
        });
    };
    const fileReport = async () => {
        try {
            await api('POST', `/api/users/${other.id}/report`, {
                reason: reportReason,
                detail: `[from chat] ${reportDetail.trim()}`.slice(0, 500),
            });
            setNotice({ title: 'Report filed', message: 'An admin will review it. You can also block them so they can’t reach you.' });
        } catch (e) {
            setNotice({ title: 'Report not filed', message: e.message });
        }
    };

    if (!user) return <LoginGate title="Messages" message="Log in to chat with other traders." />;

    const openChat = (id) => setParams(box !== 'inbox' ? { box, c: id } : { c: id });
    const setBox = (b) => setParams(b === 'inbox' ? {} : { box: b });
    const paneOpen = !!(convId || toUserId);

    // ---------- render: one chat row ----------
    const row = (c) => (
        <li key={c.id}>
            <button className={`msg-row${c.id === convId ? ' active' : ''}${c.unread ? ' unread' : ''}`} onClick={() => openChat(c.id)}>
                <Avatar user={c.other} size={40} />
                <span className="msg-row-main">
                    <span className="msg-row-top">
                        <span className="msg-row-name">{c.other?.username}</span>
                        <span className="msg-row-time">{listTime(c.lastAt)}</span>
                    </span>
                    <span className="msg-row-last">
                        {c.blocked ? <em>Blocked</em>
                            : <>{c.last?.fromMe && 'You: '}{c.last?.text}</>}
                    </span>
                </span>
                <span className="msg-row-flags">
                    {c.muted && <span className="msg-flag" title="Muted">muted</span>}
                    {c.pending && <span className="msg-flag" title="Waiting for them to accept">pending</span>}
                    {c.declined && <span className="msg-flag bad" title="They declined your message request">declined</span>}
                    {c.unread > 0 && <span className="msg-unread">{c.unread}</span>}
                </span>
            </button>
        </li>
    );

    // ---------- render: the thread ----------
    const lastMineSeen = (() => {
        if (!thread) return null;
        const mine = messages.filter(m => m.senderId === user.id);
        const last = mine[mine.length - 1];
        return last && thread.otherLastReadId >= last.id ? last.id : null;
    })();

    const renderMessages = () => {
        const out = [];
        let day = null;
        messages.forEach((m, i) => {
            const d = dayLabel(m.createdAt);
            if (d !== day) {
                day = d;
                out.push(<li key={`d-${m.id}`} className="msg-day"><span>{d}</span></li>);
            }
            const mine = m.senderId === user.id;
            const next = messages[i + 1];
            const groupEnd = !next || next.senderId !== m.senderId || dayLabel(next.createdAt) !== d;
            out.push(
                <li key={m.id} className={`msg-line${mine ? ' mine' : ''}${groupEnd ? ' end' : ''}`}>
                    {/* Sent messages are final: no editing or deleting */}
                    <div className="msg-bubble" title={timeLabel(m.createdAt)}>
                        {m.body}
                        <span className="msg-meta">{timeLabel(m.createdAt)}</span>
                    </div>
                    {m.id === lastMineSeen && <span className="msg-seen">Seen</span>}
                </li>
            );
        });
        return out;
    };

    const composer = (enabled, reason, placeholder) => (
        <div className="msg-composer">
            {!enabled ? (
                <p className="msg-composer-off">{reason}</p>
            ) : (
                <>
                    <textarea
                        value={draft}
                        placeholder={placeholder}
                        rows={1}
                        onChange={e => onDraft(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                        }}
                        aria-label="Message"
                    />
                    <button className="msg-send" onClick={send} disabled={!draft.trim() || sending} aria-label="Send">
                        <Icon name="send" size={18} />
                    </button>
                </>
            )}
            {sendError && <p className="msg-send-error">{sendError}</p>}
            {enabled && draft.length > MAX_LEN - 200 && (
                <p className="msg-count">{MAX_LEN - draft.length} characters left</p>
            )}
        </div>
    );

    const threadHead = (u, extra) => (
        <header className="msg-head">
            <button className="msg-back" onClick={() => setParams(box !== 'inbox' ? { box } : {})} aria-label="Back to chats">
                <Icon name="back" size={20} />
            </button>
            {u && (
                <Link to={`/profile/${u.id}`} className="msg-head-who">
                    <Avatar user={u} size={34} />
                    <span className="msg-head-name">{u.username}</span>
                </Link>
            )}
            {extra}
        </header>
    );

    let pane;
    if (convId) {
        if (error) {
            pane = <div className="msg-empty-pane">{error}</div>;
        } else if (!thread) {
            pane = <div className="msg-empty-pane">Loading&hellip;</div>;
        } else {
            pane = (
                <>
                    {threadHead(thread.other, (
                        <div className="msg-menu-wrap">
                            <button
                                className="msg-icon-btn"
                                aria-label="Chat options"
                                onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
                            >
                                <Icon name="more" size={20} />
                            </button>
                            {menuOpen && (
                                <div className="msg-menu" onClick={e => e.stopPropagation()}>
                                    <button onClick={() => navigate(`/profile/${thread.other.id}`)}>View binder</button>
                                    {!thread.blocked && (
                                        <button onClick={() => navigate('/trade', { state: { ownerId: thread.other.id } })}>Send trade offer</button>
                                    )}
                                    <button onClick={() => { setMenuOpen(false); act(() => api('PATCH', `/api/messages/conversations/${convId}`, { muted: !thread.muted })); }}>
                                        {thread.muted ? 'Unmute' : 'Mute'}
                                    </button>
                                    <button onClick={() => { setMenuOpen(false); act(() => api('PATCH', `/api/messages/conversations/${convId}`, { archived: !thread.archived })); }}>
                                        {thread.archived ? 'Move to inbox' : 'Archive'}
                                    </button>
                                    <button onClick={() => { setMenuOpen(false); deleteChat(); }}>Delete chat</button>
                                    <hr />
                                    {thread.blocked === 'by_me'
                                        ? <button onClick={() => { setMenuOpen(false); unblock(); }}>Unblock</button>
                                        : <button className="danger" onClick={() => { setMenuOpen(false); block(); }}>Block</button>}
                                    <button className="danger" onClick={() => { setMenuOpen(false); report(); }}>Report</button>
                                </div>
                            )}
                        </div>
                    ))}

                    {thread.request && !thread.blocked && (
                        <div className="msg-banner">
                            <span><strong>{thread.other.username}</strong> wants to chat. Accept to keep talking, or decline.</span>
                            <span className="msg-banner-actions">
                                <button className="cta-gold" onClick={() => act(() => api('POST', `/api/messages/conversations/${convId}/accept`))}>Accept</button>
                                <button className="msg-link" onClick={decline}>Decline</button>
                                <button className="msg-link danger" onClick={block}>Block</button>
                            </span>
                        </div>
                    )}
                    {thread.blocked === 'by_me' && (
                        <div className="msg-banner">
                            <span>You blocked {thread.other.username}.</span>
                            <button className="msg-link strong" onClick={unblock}>Unblock</button>
                        </div>
                    )}

                    <ul className="msg-thread" ref={scrollRef} onScroll={onScroll}>
                        {loadingOlder && <li className="msg-day"><span>Loading earlier messages&hellip;</span></li>}
                        {!hasMoreOlder && messages.length > 0 && (
                            <li className="msg-day start"><span>Start of your chat with {thread.other.username}</span></li>
                        )}
                        {renderMessages()}
                        {thread.typing && (
                            <li className="msg-line">
                                <div className="msg-bubble typing" aria-label={`${thread.other.username} is typing`}>
                                    <i /><i /><i />
                                </div>
                            </li>
                        )}
                    </ul>

                    {composer(
                        thread.canSend,
                        thread.pending
                            ? `Message request sent. You can write more once ${thread.other.username} accepts.`
                            : thread.reason,
                        `Message ${thread.other.username}`
                    )}
                </>
            );
        }
    } else if (toUserId) {
        pane = !starter ? (
            <div className="msg-empty-pane">Loading&hellip;</div>
        ) : (
            <>
                {threadHead(starter.other)}
                <div className="msg-thread msg-new">
                    {starter.other && (
                        <div className="msg-new-intro">
                            <Avatar user={starter.other} size={64} />
                            <strong>{starter.other.username}</strong>
                            {starter.canStart && (
                                <p>
                                    You get one message. It goes to their message requests, and they choose to accept or decline.
                                    {!starter.linksAllowed && ' No links until they accept.'}
                                </p>
                            )}
                        </div>
                    )}
                </div>
                {composer(starter.canStart, starter.reason, starter.other ? `Message ${starter.other.username}` : '')}
            </>
        );
    } else {
        pane = (
            <div className="msg-empty-pane">
                <Icon name="chat" size={40} />
                <p>Pick a chat, or message a trader from their profile.</p>
            </div>
        );
    }

    return (
        <div className="msg-page">
            <header className="page-section-head">
                <h1>Messages {box !== 'inbox' && <span className="page-section-current">{box === 'requests' ? 'Requests' : 'Archived'}</span>}</h1>
            </header>

            <div className={`msg-layout${paneOpen ? ' pane-open' : ''}`}>
                <aside className="msg-list">
                    <div className="msg-tabs" role="tablist">
                        {[['inbox', 'Chats'], ['requests', 'Requests'], ['archived', 'Archived']].map(([b, label]) => (
                            <button key={b} role="tab" aria-selected={box === b} className={box === b ? 'on' : ''} onClick={() => setBox(b)}>
                                {label}
                                {b === 'requests' && requestCount > 0 && <span className="msg-unread">{requestCount}</span>}
                            </button>
                        ))}
                    </div>
                    <label className="msg-search">
                        <Icon name="search" size={16} />
                        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search chats" aria-label="Search chats" />
                    </label>
                    {list === null ? (
                        <p className="msg-list-empty">Loading&hellip;</p>
                    ) : list.length === 0 ? (
                        <p className="msg-list-empty">
                            {q ? 'No chats match.'
                                : box === 'requests' ? 'No message requests.'
                                    : box === 'archived' ? 'Nothing archived.'
                                        : 'No chats yet. Message a trader from their profile to start one.'}
                        </p>
                    ) : (
                        <ul className="msg-rows">{list.map(row)}</ul>
                    )}
                </aside>

                <section className="msg-pane">{pane}</section>
            </div>

            <ConfirmDialog
                open={!!dialog}
                title={dialog?.title}
                confirmLabel={dialog?.confirmLabel}
                danger={dialog?.danger}
                onCancel={() => setDialog(null)}
                onConfirm={() => {
                    const d = dialog;
                    setDialog(null);
                    if (d.kind === 'report') fileReport(); else d.action();
                }}
                message={dialog?.kind === 'report' ? (
                    <div className="msg-report-form">
                        <label>
                            What happened?
                            <select value={reportReason} onChange={e => setReportReason(e.target.value)}>
                                {REPORT_REASONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                            </select>
                        </label>
                        <textarea
                            placeholder="Optional: anything an admin should know"
                            maxLength={480}
                            rows={3}
                            value={reportDetail}
                            onChange={e => setReportDetail(e.target.value)}
                        />
                    </div>
                ) : dialog?.message}
            />
            <ConfirmDialog
                open={!!notice}
                title={notice?.title}
                message={notice?.message}
                onCancel={() => setNotice(null)}
            />
        </div>
    );
}

export default Messages;
